/**
 * Payment Gate Global Tool
 *
 * Collects payment from a user before proceeding with a workflow.
 * Shows an inline invoice form in the chat with support for:
 * - One-click payment with saved methods
 * - Apple Pay / Google Pay (via Stripe automatic_payment_methods)
 * - New card/bank account entry
 *
 * This tool is typically triggered programmatically by the orchestrator
 * or as part of a workflow chain, not by user text input.
 */

import { getAuth } from 'firebase/auth';
import { stripeApi } from '../../stripeClient';
import { hasNearbyNegation } from '../triggerHelpers';
import type {
  GlobalToolDefinition,
  StepContext,
  StepResult,
  ToolTheme,
} from '../types';

// ============================================================================
// Tool State
// ============================================================================

/**
 * State for the payment gate tool.
 */
export interface PaymentGateState {
  /** Current stage of the payment flow */
  stage: 'idle' | 'awaiting_payment' | 'payment_complete' | 'payment_failed' | 'payment_cancelled';
  /** Amount to charge in USD */
  amountUsd: number;
  /** User being charged */
  targetUid: string;
  /** Invoice description */
  description: string;
  /** Invoice title */
  invoiceTitle: string;
  /** Allowed payment methods */
  allowedMethods: ('card' | 'bank' | 'ach')[];
  /** Stripe PaymentIntent ID */
  paymentIntentId: string | null;
  /** Stripe client secret */
  clientSecret: string | null;
  /** Saved payment methods (cards + bank accounts) */
  savedMethods: {
    cards: Array<{ id: string; brand: string; last4: string; expMonth: number; expYear: number; isDefault: boolean }>;
    bankAccounts: Array<{ id: string; bankName: string; last4: string; accountType: string; isDefault: boolean }>;
  } | null;
  /** Custom metadata to pass through */
  metadata: Record<string, any> | null;
  /** Payment method used (set on success) */
  paymentMethod?: string;
  /** Amount paid (set on success) */
  amountPaid?: number;
}

// ============================================================================
// Tool Theme
// ============================================================================

const PAYMENT_GATE_THEME: ToolTheme = {
  accent: '#059669',      // Green 600
  accentBg: '#d1fae5',    // Green 100
  border: '#10b981',      // Green 500
  subtitle: '#475569',    // Slate 600
  muted: '#64748b',       // Slate 500
};

// ============================================================================
// Button IDs
// ============================================================================

export const BUTTON_IDS = {
  pay: 'payment-gate-pay-btn',
  cancel: 'payment-gate-cancel-btn',
};

// ============================================================================
// Input Types
// ============================================================================

export interface PaymentGateInput {
  /** Amount to charge in USD (required) */
  amountUsd: number;
  /** User to charge - defaults to current user */
  targetUid?: string;
  /** Invoice description shown to user */
  description?: string;
  /** Invoice title */
  invoiceTitle?: string;
  /** Allowed payment methods - defaults to all */
  allowedMethods?: ('card' | 'bank' | 'ach')[];
  /** Custom metadata passed through to output */
  metadata?: Record<string, any>;
  /** Whether this is a chain step from orchestrator */
  isChainStep?: boolean;
}

export interface PaymentGateOutput {
  /** Whether payment was successful */
  paymentVerified: boolean;
  /** Stripe PaymentIntent ID */
  paymentIntentId: string | null;
  /** Amount paid in USD */
  amountPaid: number;
  /** Payment method used */
  paymentMethod: string;
  /** Pass-through metadata */
  metadata: Record<string, any> | null;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

// ============================================================================
// Trigger Pattern
// ============================================================================

/**
 * Match "charge $X to Y" or "payment required" patterns.
 * This tool is typically triggered programmatically, not by user text.
 *
 * Phase 0 hardening: each match is run through the in-clause negation
 * guard so phrases like "don't charge $50" or "payment NOT required"
 * are suppressed.
 */
function matchPaymentGateTrigger(text: string): { matches: boolean; amountUsd?: number; targetUser?: string } {
  const trimmed = (text || '').trim();
  if (!trimmed) return { matches: false };

  // Match "charge $50 to user@email.com" or "charge 50 to userId"
  const chargeMatch = trimmed.match(/^charge\s+\$?([\d.]+)\s+to\s+(\S+)/i);
  if (chargeMatch && typeof chargeMatch.index === 'number') {
    if (!hasNearbyNegation(trimmed, chargeMatch.index, chargeMatch[0].length)) {
      return {
        matches: true,
        amountUsd: parseFloat(chargeMatch[1]),
        targetUser: chargeMatch[2],
      };
    }
  }

  // Match "payment required $50" or "require payment $50"
  const paymentMatch = trimmed.match(/(?:payment\s+required|require\s+payment)\s+\$?([\d.]+)/i);
  if (paymentMatch && typeof paymentMatch.index === 'number') {
    if (!hasNearbyNegation(trimmed, paymentMatch.index, paymentMatch[0].length)) {
      return {
        matches: true,
        amountUsd: parseFloat(paymentMatch[1]),
      };
    }
  }

  return { matches: false };
}

// ============================================================================
// Tool Definition
// ============================================================================

export const paymentGateTool: GlobalToolDefinition<PaymentGateState> = {
  id: 'payment-gate',
  name: 'Payment Gate',
  description: 'Collect payment from a user before proceeding with workflow',

  triggerPattern: matchPaymentGateTrigger,

  createInitialState: (): PaymentGateState => ({
    stage: 'idle',
    amountUsd: 0,
    targetUid: '',
    description: '',
    invoiceTitle: 'Payment Required',
    allowedMethods: ['card', 'bank', 'ach'],
    paymentIntentId: null,
    clientSecret: null,
    savedMethods: null,
    metadata: null,
  }),

  steps: [
    // ========================================================================
    // Step 1: Initialize Payment - Create PaymentIntent and show invoice
    // ========================================================================
    {
      id: 'initialize-payment',
      name: 'Initialize Payment',
      activeDisplayName: 'preparing invoice',

      execute: async (
        ctx: StepContext<PaymentGateState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        const input = rawInput as PaymentGateInput | undefined;

        // ====================================================================
        // Validate required inputs
        // ====================================================================
        const amountUsd = input?.amountUsd || 0;
        if (amountUsd <= 0) {
          ctx.messaging.showError(
            'Invalid Amount',
            'A valid payment amount is required to proceed.'
          );
          return { status: 'failed', error: new Error('Invalid amount: must be positive') };
        }

        // Get target user - default to current authenticated user
        const currentUser = getAuth().currentUser;
        const targetUid = input?.targetUid || currentUser?.uid || '';
        if (!targetUid) {
          ctx.messaging.showError(
            'User Not Found',
            'Could not determine which user to charge.'
          );
          return { status: 'failed', error: new Error('Target user not specified') };
        }

        const description = input?.description || '';
        const invoiceTitle = input?.invoiceTitle || 'Payment Required';
        const allowedMethods = input?.allowedMethods || ['card', 'bank', 'ach'];
        const metadata = input?.metadata || null;

        // ====================================================================
        // Create PaymentIntent via backend
        // ====================================================================
        console.log('[PaymentGate] Creating PaymentIntent', { amountUsd, targetUid });

        const result = await stripeApi.createWorkflowPaymentIntent({
          amountUsd,
          targetUid,
          workflowId: 'payment-gate',
          stepId: 'initialize-payment',
          description,
          allowedMethods,
          metadata: metadata || undefined,
          invoiceTitle,
        });

        if (result.error || !result.clientSecret || !result.paymentIntentId) {
          const errorMsg = result.error || 'Failed to create payment';
          ctx.messaging.showError('Payment Setup Failed', errorMsg);
          return { status: 'failed', error: new Error(errorMsg) };
        }

        console.log('[PaymentGate] PaymentIntent created', { paymentIntentId: result.paymentIntentId });

        // ====================================================================
        // Update state
        // ====================================================================
        ctx.setState({
          stage: 'awaiting_payment',
          amountUsd,
          targetUid,
          description,
          invoiceTitle,
          allowedMethods,
          paymentIntentId: result.paymentIntentId,
          clientSecret: result.clientSecret,
          savedMethods: result.savedMethods || null,
          metadata,
        });

        // ====================================================================
        // Show inline invoice message in chat
        // ====================================================================
        // The messaging system will render this as an InlineInvoiceMessage component
        ctx.messaging.showPaymentForm({
          amountUsd,
          description,
          invoiceTitle,
          clientSecret: result.clientSecret,
          paymentIntentId: result.paymentIntentId,
          targetUid,
          allowedMethods,
          savedMethods: result.savedMethods || { cards: [], bankAccounts: [] },
        });

        // ====================================================================
        // Return awaiting-input to pause workflow
        // ====================================================================
        return {
          status: 'awaiting-input',
          carryForward: {
            paymentIntentId: result.paymentIntentId,
            clientSecret: result.clientSecret,
            amountUsd,
            targetUid,
            metadata,
          },
        };
      },
    },

    // ========================================================================
    // Step 2: Verify Payment - Called when payment status changes
    // ========================================================================
    {
      id: 'verify-payment',
      name: 'Verify Payment',
      activeDisplayName: 'verifying payment',

      execute: async (
        ctx: StepContext<PaymentGateState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        // userInput will be set by the InlineInvoiceMessage component via the runtime
        // when the Firestore listener detects a status change
        const userInput = ctx.userInput as string | undefined;

        console.log('[PaymentGate] Verify step - userInput:', userInput);

        // ====================================================================
        // Handle cancellation
        // ====================================================================
        if (userInput === 'cancel' || userInput === 'cancelled') {
          ctx.setState({ stage: 'payment_cancelled' });
          ctx.messaging.showWarning(
            'Payment Cancelled',
            'The payment was cancelled. The workflow will not continue.'
          );
          return {
            status: 'completed',
            nextStep: null, // End workflow
            carryForward: {
              paymentVerified: false,
              paymentIntentId: ctx.state.paymentIntentId,
              amountPaid: 0,
              paymentMethod: '',
              metadata: ctx.state.metadata,
            } as PaymentGateOutput,
          };
        }

        // ====================================================================
        // Handle success
        // ====================================================================
        if (userInput === 'succeeded' || userInput === 'paid') {
          ctx.setState({
            stage: 'payment_complete',
            paymentMethod: 'card', // Will be updated by actual payment method
            amountPaid: ctx.state.amountUsd,
          });

          ctx.messaging.showSuccess(
            'Payment Successful',
            `$${ctx.state.amountUsd.toFixed(2)} has been charged successfully.`
          );

          // Show appointment booking widget if configured in metadata
          const metadata = ctx.state.metadata;
          if (metadata?.showAppointmentBooking) {
            const appointmentConfig = metadata.appointmentConfig || {};
            ctx.messaging.showAppointmentBooking({
              iframeUrl: appointmentConfig.iframeUrl || 'https://appointmentshidalgolaw.as.me/schedule/a7a751b8/appointment/25546710',
              title: appointmentConfig.title || 'Book Your Consultation',
              instructions: appointmentConfig.instructions || 'Your payment has been received! Please select a time that works best for you to schedule your consultation.',
            });
          }

          return {
            status: 'completed',
            carryForward: {
              paymentVerified: true,
              paymentIntentId: ctx.state.paymentIntentId,
              amountPaid: ctx.state.amountUsd,
              paymentMethod: ctx.state.paymentMethod || 'card',
              metadata: ctx.state.metadata,
            } as PaymentGateOutput,
          };
        }

        // ====================================================================
        // Handle failure
        // ====================================================================
        const errorMessage = typeof userInput === 'string' && userInput !== 'failed'
          ? userInput
          : 'The payment could not be processed.';

        ctx.setState({ stage: 'payment_failed' });
        ctx.messaging.showError('Payment Failed', errorMessage);

        return {
          status: 'failed',
          error: new Error(errorMessage),
          carryForward: {
            paymentVerified: false,
            paymentIntentId: ctx.state.paymentIntentId,
            amountPaid: 0,
            paymentMethod: '',
            metadata: ctx.state.metadata,
          } as PaymentGateOutput,
        };
      },
    },
  ],

  theme: PAYMENT_GATE_THEME,

  buttonIds: BUTTON_IDS,
};

export default paymentGateTool;
